import { useState } from "react";
import { useHideCrisp } from "@/hooks/use-hide-crisp";
import { useRoute } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle2, Loader2, ClipboardCheck, AlertCircle, Home, User, MessageSquare, Send, ArrowLeft, Sparkles } from "lucide-react";
import type { ServiceFormTemplate, ServiceFormLink, ServiceFormSubmissionMessage } from "@shared/schema";
import { usePageTitle } from "@/hooks/use-page-title";
import { ServiceFormFill, type ServiceFormFieldError, type ServiceFormSubmitError, type ServiceFormSubmitPayload } from "@/components/service-form-fill";

// Public tokenized fill page — member / phone magic-link recipients.
// The fill UI itself lives in the shared <ServiceFormFill> (also used by the
// vendor portal's form-task detail); this page owns the token transport,
// header chrome, submitted state, and the public chat thread.
export default function ServiceFormPage() {
  useHideCrisp();
  usePageTitle("Service Form");
  const [, params] = useRoute("/service-form/:token");
  const token = params?.token || "";

  const [submitted, setSubmitted] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  const { data, isLoading, isError, error } = useQuery<{
    link: ServiceFormLink & { vendorId?: number | null };
    template: ServiceFormTemplate;
    alreadySubmitted: boolean;
    propertyAddress: string | null;
    vendorName: string | null;
    recipientKind?: "vendor" | "member_direct" | "member_padsplit" | "phone";
  }>({
    queryKey: ["/api/public/service-form", token],
    queryFn: async () => {
      const res = await fetch(`/api/public/service-form/${token}`);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Form not found");
      }
      return res.json();
    },
    enabled: !!token,
  });

  const getUploadUrl = async (): Promise<{ uploadUrl: string; objectPath: string }> => {
    const urlRes = await fetch(`/api/public/service-form/${token}/upload-url`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    });
    if (!urlRes.ok) {
      const text = await urlRes.text();
      throw new Error(`${urlRes.status}: ${text}`);
    }
    return urlRes.json();
  };

  const submitForm = async (payload: ServiceFormSubmitPayload) => {
    const res = await fetch(`/api/public/service-form/${token}/submit`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json() as { message?: string; errors?: ServiceFormFieldError[] };
      const submitError = new Error(err.message || "Submission failed") as ServiceFormSubmitError;
      submitError.fieldErrors = err.errors;
      throw submitError;
    }
    return res.json();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (isError || !data) {
    const errorMessage = (error as Error)?.message || "This form link may have expired or been removed.";
    const isExpired = errorMessage.includes("This form link has expired");
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">{isExpired ? "Form Link Expired" : "Form Not Found"}</h2>
            <p className="text-sm text-gray-500">{errorMessage}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const recipientKind = data.recipientKind ?? (data.link.vendorId ? "vendor" : "phone");

  if (data.alreadySubmitted || submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4" data-testid="page-service-form-submitted">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {recipientKind === "vendor" ? "Form Submitted!" : "Your form has been completed"}
            </h2>
            <p className="text-sm text-gray-500">
              {recipientKind === "vendor"
                ? `Thank you for completing the ${data.template.name}. Your submission has been recorded.`
                : `Thank you! Your ${data.template.name} has been recorded.`}
            </p>
            {recipientKind === "vendor" && (
              <>
                <div className="mt-6">
                  <ChatThread token={token} hostName="Host" />
                </div>
                <Button
                  className="mt-6 w-full bg-brand hover:bg-brand-hover text-white"
                  onClick={() => { window.location.href = "/vendor-portal"; }}
                  data-testid="button-return-to-vendor-portal"
                >
                  Return to vendor portal
                </Button>
              </>
            )}
            {recipientKind === "member_direct" && (
              <Button
                className="mt-6 w-full bg-brand hover:bg-brand-hover text-white"
                onClick={() => { window.location.href = "/member"; }}
                data-testid="button-return-to-member-portal"
              >
                Go to member portal
              </Button>
            )}
            {recipientKind === "member_padsplit" && (
              <Button
                className="mt-6 w-full bg-brand hover:bg-brand-hover text-white"
                onClick={() => { window.location.href = `/cleaning/${encodeURIComponent(token)}`; }}
                data-testid="button-view-padsplit-cleaning"
              >
                <Sparkles className="mr-2 h-4 w-4" />
                View cleaning schedule
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const template = data.template;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="page-service-form">
      <div className="bg-white border-b px-4 py-4 sticky top-0 z-10">
        <div className="max-w-lg mx-auto">
          {recipientKind === "vendor" && (
            <div className="flex items-center justify-between mb-2">
              <Button
                variant="ghost"
                size="sm"
                className="-ml-2 text-brand hover:text-brand-hover h-auto py-1 px-2"
                onClick={() => { window.location.href = "/vendor-portal"; }}
                data-testid="button-back-to-vendor-portal"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to vendor portal
              </Button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-brand" />
            <h1 className="text-lg font-bold text-gray-900" data-testid="text-form-title">{template.name}</h1>
          </div>
          {template.description && (
            <p className="text-xs text-gray-500 mt-0.5">{template.description}</p>
          )}
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-4 pb-32">
        {recipientKind === "member_padsplit" && (
          <Card className="border-brand/20 bg-brand-muted/30">
            <CardContent className="flex items-center justify-between gap-3 py-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900">Your cleaning schedule</p>
                <p className="text-xs text-gray-600">View the house rotation or request an opt-out.</p>
              </div>
              <Button
                size="sm"
                className="shrink-0 bg-brand hover:bg-brand-hover text-white"
                onClick={() => { window.location.href = `/cleaning/${encodeURIComponent(token)}`; }}
                data-testid="button-view-padsplit-cleaning"
              >
                View
              </Button>
            </CardContent>
          </Card>
        )}
        {(data.propertyAddress || data.vendorName) && (
          <Card data-testid="card-assignment-info">
            <CardContent className="py-4 space-y-2">
              {data.propertyAddress && (
                <div className="flex items-center gap-2" data-testid="text-property-address">
                  <Home className="h-4 w-4 text-gray-400 shrink-0" />
                  <span className="text-sm font-medium text-gray-700">Property:</span>
                  <span className="text-sm text-gray-900">{data.propertyAddress}</span>
                </div>
              )}
              {data.vendorName && (
                <div className="flex items-center gap-2" data-testid="text-vendor-name">
                  <User className="h-4 w-4 text-gray-400 shrink-0" />
                  <span className="text-sm font-medium text-gray-700">Vendor:</span>
                  <span className="text-sm text-gray-900">{data.vendorName}</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <ServiceFormFill
          template={template}
          defaultSubmitterName={data.link.recipientName}
          getUploadUrl={getUploadUrl}
          submit={submitForm}
          onSubmitted={() => setSubmitted(true)}
          footerExtra={data.link.vendorId ? (
            <Button
              className="flex-1 bg-brand-accent hover:bg-[#E69500] text-white"
              onClick={() => setChatOpen(true)}
              data-testid="button-open-chat-with-host"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Chat with Host
            </Button>
          ) : undefined}
        />
      </div>

      <Dialog open={chatOpen} onOpenChange={setChatOpen}>
        <DialogContent className="max-w-md" data-testid="dialog-chat-with-host">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-brand" />
              Chat with Host
            </DialogTitle>
          </DialogHeader>
          <ChatThread token={token} hostName="Host" />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ChatThread({ token, hostName }: { token: string; hostName: string }) {
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const messagesKey = ["/api/public/service-form", token, "messages"] as const;
  const messagesQuery = useQuery<ServiceFormSubmissionMessage[]>({
    queryKey: messagesKey as unknown as readonly unknown[],
    queryFn: async () => {
      const res = await fetch(`/api/public/service-form/${token}/messages`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load messages");
      return res.json();
    },
    refetchInterval: 15_000,
    enabled: !!token,
  });
  const sendMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/public/service-form/${token}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ body }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { message?: string })?.message || "Failed to send message");
      }
      return res.json();
    },
    onSuccess: () => {
      setBody("");
      qc.invalidateQueries({ queryKey: messagesKey as unknown as readonly unknown[] });
    },
  });
  const messages = messagesQuery.data || [];
  return (
    <div className="space-y-3" data-testid="thread-public-form-chat">
      <div className="max-h-72 overflow-y-auto space-y-2 rounded border bg-gray-50 p-3">
        {messagesQuery.isLoading ? (
          <p className="text-xs text-gray-400 text-center py-4">Loading messages...</p>
        ) : messages.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-4">
            No messages yet. Send the host a question or note about this form.
          </p>
        ) : (
          messages.map((m) => {
            const isVendor = m.senderType === "vendor_owner" || m.senderType === "vendor_member";
            const senderName = (m as ServiceFormSubmissionMessage & { senderName?: string }).senderName
              || (isVendor ? "You" : hostName);
            const attachments: string[] = Array.isArray(m.attachments) ? (m.attachments as string[]) : [];
            return (
              <div
                key={m.id}
                className={`flex ${isVendor ? "justify-end" : "justify-start"}`}
                data-testid={`msg-public-form-${m.id}`}
              >
                <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${isVendor ? "bg-brand text-white" : "bg-white border text-gray-900"}`}>
                  <p className={`text-[10px] font-medium mb-1 ${isVendor ? "text-blue-100" : "text-gray-500"}`}>{senderName}</p>
                  <p className="whitespace-pre-wrap break-words text-left">{m.body}</p>
                  {attachments.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {attachments.map((url, i) => (
                        <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                          <img src={url} alt="attachment" className="h-16 w-16 object-cover rounded border" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
      <div className="flex gap-2">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Type a message to the host..."
          rows={2}
          className="flex-1 text-sm"
          data-testid="textarea-public-form-chat"
        />
        <Button
          type="button"
          onClick={() => sendMutation.mutate()}
          disabled={!body.trim() || sendMutation.isPending}
          className="bg-brand hover:bg-brand-hover text-white self-end"
          data-testid="button-send-public-form-chat"
        >
          {sendMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
      {sendMutation.isError && (
        <p className="text-xs text-red-500" data-testid="text-public-form-chat-error">
          {(sendMutation.error as Error)?.message}
        </p>
      )}
    </div>
  );
}
